<script lang="ts" setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';

import yardTasksService from '@satellite/services/yard-tasks-service';
import commentService from '@satellite/services/comment-service';

import { useDialogListener, useNotify } from '@/composables';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { YardTaskFlags, type TaskViewData } from '@nova/core';
import { addTag } from '@satellite/helpers/yardTaskUtils';

const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-report-issue-dialog');

const { trackYardTaskFlaggedIssue } = useYardTaskAnalytics();

const { notify } = useNotify();

const dialogOpenModel = computed({
  get: () => isDialogOpen.value,
  set: (value: boolean) => {
    if (!value) closeDialog();
  }
});

const selectedComment = ref<string | null>(null);
const customComment = ref('');
const isLoading = ref(false);

const isOtherComment = computed(() => {
  return selectedComment.value === 'Other...';
});

const commentOptions = computed(() => {
  if (dialogProps.value?.type === 'move') {
    return [
      'Asset requires maintenance before moving.',
      'Destination spot is unavailable.',
      "Asset is stuck, damaged or can't be moved.",
      'Other...'
    ];
  }
  return [];
});

const currentTags = computed(() => {
  if (Array.isArray(dialogProps.value?.tags)) {
    return dialogProps.value.tags;
  }

  return [];
});

const canConfirm = computed(() => {
  if (!selectedComment.value) {
    return false;
  }

  if (isOtherComment.value) {
    return customComment.value.trim().length > 0;
  }

  return true;
});

const getCommentText = (): string => {
  if (!selectedComment.value) {
    return '';
  }

  if (isOtherComment.value) {
    return customComment.value.trim();
  }

  return selectedComment.value;
};

const handleConfirm = async () => {
  if (!dialogProps.value?.taskId) {
    console.error('Failed to report issue: No task ID found');
    notify('Failed to report issue: Missing task information', 'error');
    return;
  }

  if (!selectedComment.value) {
    return;
  }

  if (!canConfirm.value) {
    return;
  }

  isLoading.value = true;

  const taskId = dialogProps.value.taskId;
  const comment = getCommentText();

  try {
    // Update task with ISSUE flag
    const updatedFlags = addTag(currentTags.value, YardTaskFlags.ISSUE);
    const payload = {
      tags: updatedFlags
    };

    await yardTasksService.updateYardTask(taskId, payload);

    // Create comment
    await commentService.createComment({
      htmlContent: comment,
      relatedEntity: 'YardTask',
      relatedId: taskId
    });

    notify('Issue reported successfully', 'success');

    const taskType = dialogProps.value.type === 'move' ? 'move' : 'check';
    const taskObject: Partial<TaskViewData> = {
      id: dialogProps.value.taskId,
      type: taskType,
      tags: updatedFlags
    };
    try {
      trackYardTaskFlaggedIssue(taskObject);
    } catch (analyticsError) {
      console.error('Failed to track yard task flagged issue:', analyticsError);
    }

    try {
      dialogProps.value?.onIssueReported?.(comment);
    } catch (callbackError) {
      console.error('Error in onIssueReported callback:', callbackError);
      // Don't notify user about callback errors, the main operation succeeded
    }

    closeDialog();
  } catch (error) {
    console.error('Failed to report issue for task', error);
    notify('Failed to report issue', 'error');
  } finally {
    // Always reset loading state
    isLoading.value = false;
  }
};

const resetForm = () => {
  selectedComment.value = null;
  customComment.value = '';
};

const handleAfterLeave = () => {
  resetForm();
  resetDialogProps();
};

watch(
  () => isDialogOpen.value,
  newVal => {
    if (newVal) {
      resetForm();
    }
  }
);

onMounted(() => {
  subscribeToDialogEvent();
});

onBeforeUnmount(() => {
  unsubscribeFromDialogEvent();
});
</script>

<template>
  <v-dialog
    class="report-issue-dialog"
    v-model="dialogOpenModel"
    @keydown.escape="closeDialog"
    @after-leave="handleAfterLeave"
    aria-labelledby="report-issue-title"
    transition="dialog-bottom-transition"
    fullscreen
    style="top: 5%">
    <v-card>
      <v-card-title
        class="d-flex flex-column align-center gap-3 px-0 py-8 border-b border-color-line-border">
        <v-icon icon="mdi-alert-circle" color="error" size="24"></v-icon>
        <h2
          id="report-issue-title"
          class="typography-heading-md-bold typography-md-heading-lg-bold">
          Mark task as having an issue?
        </h2>
      </v-card-title>

      <v-card-text class="py-6 px-5 d-flex flex-column">
        <p class="typography-body-md-bold mb-6 text-center text--color-text-secondary">
          Include a comment:
        </p>

        <div class="d-flex flex-column gap-2">
          <v-radio-group v-model="selectedComment" hide-details class="mt-0">
            <v-radio
              v-for="option in commentOptions"
              :key="option"
              :value="option"
              :label="option"
              class="report-issue-radio"></v-radio>
          </v-radio-group>

          <v-textarea
            v-if="isOtherComment"
            v-model="customComment"
            placeholder="Type a comment..."
            color="primary"
            variant="outlined"
            density="compact"
            rows="3"
            hide-details></v-textarea>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex flex-column gap-3 gap-md-4 align-stretch flex-md-row pa-4">
        <v-btn
          variant="elevated"
          color="primary"
          size="large"
          class="flex-fill typography-button-lg order-1 order-md-2"
          :disabled="!canConfirm || isLoading"
          @click="handleConfirm"
          :loading="isLoading">
          Confirm
        </v-btn>
        <v-btn
          variant="outlined"
          size="large"
          class="flex-fill typography-button-lg order-2 order-md-1"
          @click="closeDialog"
          :loading="isLoading">
          Nevermind
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
:deep(.report-issue-radio) {
  .v-radio {
    align-items: flex-start;
  }

  .v-label {
    padding-left: 8px;
    white-space: normal;
    line-height: 1.4;
  }
}

:deep(.v-radio-group) {
  .v-radio {
    background-color: $color-neutral-10;
    border-radius: 4px;
    padding: $spacing-3;
    margin-bottom: $spacing-2;
    border: 1px solid $color-line-border;
    min-height: 48px;
    display: flex;
    align-items: center;
  }
}
</style>
